import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Pagination from "rc-pagination";
import "rc-pagination/assets/index.css";
import { handleSubscriptionList } from "../../services/APIClient";
import { STATUS_CODES } from "../../utils/statusCode";
import Toster from "../../utils/Toaster";
import { removeUserData } from "../../store/slices/userSlice";
import { debounce, formatDateRange } from "../../utils/CommonFunction";
import { useLoader } from "../../contexts/LoaderContext";

const SubscriptionList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setLoading } = useLoader();

  const [subscriptionList, setSubscriptionList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchSubscription = async (page, query = "") => {
    setLoading(true);
    try {
      const response = await handleSubscriptionList({
        pageNumber: page,
        search: query,
      });

      if (response?.code === STATUS_CODES.SUCCESS) {
        setSubscriptionList(response.data.subscriptionList || []);
        setTotalCount(response.data.subscriptionCount || 0);
      } else if (response?.code === STATUS_CODES.INVALID_TOKEN) {
        dispatch(removeUserData({}));
        navigate("/login");
      } else {
        Toster("Failed to fetch content", "error");
      }
    } catch (error) {
      Toster("An error occurred while fetching subscribe list", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchSubscription(currentPage, searchQuery);
  }, [currentPage, searchQuery]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const debouncedSearch = useCallback(
    debounce((query) => {
      setSearchQuery(query);
      setCurrentPage(1);
    }, 500),
    []
  );

  const handleSearch = (event) => {
    debouncedSearch(event.target.value);
  };

  return (
    <div className="w-full px-0 py-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 px-4">
        <h1 className="text-[30px] font-semibold text-[#000] mb-4 sm:mb-0 leading-[100%]">
          Subscription List
        </h1>

        <div className="relative w-full sm:w-[300px]">
          <img
            src="/search.png"
            alt="Search Icon"
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search"
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="w-full overflow-x-auto">
        <table className="min-w-[800px] w-full bg-white text-left text-sm shadow-md rounded-lg">
          <thead className="bg-[#252525] text-white sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-[16px]">Image</th>
              <th className="px-4 py-3 text-[16px]">Name</th>
              <th className="px-4 py-3 text-[16px]">Email</th>

              <th className="px-4 py-3 text-[16px]">Subscription</th>

              <th className="px-4 py-3 text-[16px]">Subscription Validity</th>

              <th className="px-4 py-3 text-[16px]">Recursive Payment</th>

              <th className="px-4 py-3 text-[16px]">Subscription Plateform</th>
            </tr>
          </thead>
          <tbody>
            {subscriptionList.length > 0 ? (
              subscriptionList.map((subscribe, index) => (
                <tr
                  key={index}
                  className="border-b"
                  style={{
                    backgroundColor: index % 2 === 1 ? "#fff" : "#F2F2F2",
                  }}
                >
                  <td className="px-4 py-3">
                    <img
                      src={subscribe?.profileImage}
                      alt={subscribe?.fullname}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  </td>
                  <td className="px-4 py-3 text-[#333333] font-medium">
                    {subscribe?.fullname}
                  </td>
                  <td className="px-4 py-3 text-[#333333] font-medium">
                    {subscribe?.email}
                  </td>

                  <td className="px-4 py-3 text-[#333333] font-normal">
                    {subscribe?.planName}
                  </td>

                  <td className="px-4 py-3 text-center text-[#333333] font-normal">
                    {formatDateRange(
                      subscribe?.planStartDate,
                      subscribe?.planEndDate
                    )}
                  </td>

                  <td className="px-4 py-3 text-center text-[#333333] font-normal">
                    {subscribe?.isRecursiveCancel === false ? "Yes" : "No"}
                  </td>

                  <td className="px-4 py-3 text-center text-[#333333] font-normal">
                    {subscribe?.subscriptionPlatform === "web"
                      ? "Website"
                      : "iOS"}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="text-center text-gray-500 px-4 py-6">
                  No officers available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex justify-end px-4">
        {subscriptionList.length > 0 && (
          <Pagination
            current={currentPage}
            total={totalCount}
            pageSize={10}
            onChange={handlePageChange}
            showSizeChanger={false}
            showQuickJumper={false}
          />
        )}
      </div>
    </div>
  );
};

export default SubscriptionList;
